"use client"

import type React from "react"

import { useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Shield, Save, RotateCcw, Zap, Lock } from "lucide-react"

interface SecurityConfig {
  allowThreshold: number
  challengeThreshold: number
  blockThreshold: number
  performanceMode: "ultra_fast" | "balanced" | "paranoid"
  enableRealTimeMonitoring: boolean
  enableIntelligenceEngine: boolean
}

interface SecurityMode {
  value: string
  label: string
  description: string
  color: string
  settings: {
    allow: number
    challenge: number
    block: number
  }
}

interface SecurityConfigContentProps {
  config: SecurityConfig
  setConfig: React.Dispatch<React.SetStateAction<SecurityConfig>>
  securityMode: string
  handleSecurityModeChange: (mode: string) => void
}

export function SecurityConfigContent({
  config,
  setConfig,
  securityMode,
  handleSecurityModeChange,
}: SecurityConfigContentProps) {
  // Safe config with defaults
  const safeConfig: SecurityConfig = {
    allowThreshold: config?.allowThreshold ?? 40,
    challengeThreshold: config?.challengeThreshold ?? 75,
    blockThreshold: config?.blockThreshold ?? 100,
    performanceMode: config?.performanceMode ?? "ultra_fast",
    enableRealTimeMonitoring: config?.enableRealTimeMonitoring ?? true,
    enableIntelligenceEngine: config?.enableIntelligenceEngine ?? true,
  }

  const securityModes: SecurityMode[] = [
    {
      value: "very_fast",
      label: "Very Fast",
      description: "Ultra lenient for maximum speed",
      color: "green",
      settings: { allow: 70, challenge: 90, block: 100 },
    },
    {
      value: "medium",
      label: "Medium",
      description: "Balanced security and performance",
      color: "amber",
      settings: { allow: 40, challenge: 75, block: 100 },
    },
    {
      value: "strict",
      label: "Strict",
      description: "Maximum security, lower performance",
      color: "red",
      settings: { allow: 25, challenge: 50, block: 85 },
    },
  ]

  // Safe config update
  const updateConfig = useCallback(
    (key: keyof SecurityConfig, value: any) => {
      try {
        if (typeof setConfig === "function") {
          setConfig((prev: SecurityConfig) => ({ ...prev, [key]: value }))
        }
      } catch (error) {
        console.error("Error updating security config:", error)
      }
    },
    [setConfig],
  )

  // Safe security mode change
  const handleModeChange = useCallback(
    (mode: string) => {
      try {
        if (typeof handleSecurityModeChange === "function") {
          handleSecurityModeChange(mode)
        }
      } catch (error) {
        console.error("Error changing security mode:", error)
      }
    },
    [handleSecurityModeChange],
  )

  // Safe reset to defaults
  const resetToDefaults = useCallback(() => {
    try {
      const defaultMode = securityModes.find((mode) => mode.value === securityMode)
      if (defaultMode && typeof setConfig === "function") {
        setConfig((prev: SecurityConfig) => ({
          ...prev,
          allowThreshold: defaultMode.settings.allow,
          challengeThreshold: defaultMode.settings.challenge,
          blockThreshold: defaultMode.settings.block,
        }))
      }
    } catch (error) {
      console.error("Error resetting to defaults:", error)
    }
  }, [securityMode, securityModes, setConfig])

  // Safe mode rendering
  const renderSecurityMode = (mode: SecurityMode) => {
    try {
      const isActive = securityMode === mode.value

      return (
        <div
          key={mode.value}
          className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all ${
            isActive
              ? `border-${mode.color}-500/50 bg-${mode.color}-500/10`
              : "border-slate-700/50 bg-slate-800/30 hover:border-slate-600/50"
          }`}
          onClick={() => handleModeChange(mode.value)}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-slate-200">{mode.label}</h3>
            {isActive && (
              <Badge className={`bg-${mode.color}-500/20 text-${mode.color}-400 border-${mode.color}-500/50`}>
                Active
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-400 mb-3">{mode.description}</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-slate-500">Allow</div>
              <div className="text-green-400 font-bold">{mode.settings.allow}%</div>
            </div>
            <div>
              <div className="text-slate-500">Challenge</div>
              <div className="text-amber-400 font-bold">{mode.settings.challenge}%</div>
            </div>
            <div>
              <div className="text-slate-500">Block</div>
              <div className="text-red-400 font-bold">{mode.settings.block}%</div>
            </div>
          </div>
        </div>
      )
    } catch (error) {
      console.error("Error rendering security mode:", error)
      return null
    }
  }

  return (
    <>
      {/* Security Mode Selector */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-green-500" />
            Security Mode Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {securityModes.map(renderSecurityMode).filter(Boolean)}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Current Mode:{" "}
              <span className="text-cyan-400 font-semibold capitalize">
                {(securityMode || "medium").replace("_", " ")}
              </span>
            </div>
            <Button
              onClick={() => {
                try {
                  console.log(`POST /api/security-mode - Setting mode to: ${securityMode}`)
                } catch (error) {
                  console.error("Error applying security mode:", error)
                }
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-1" />
              Apply Security Mode
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Threshold Configuration */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <Zap className="mr-2 h-5 w-5 text-amber-500" />
            Advanced Threshold Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-slate-300">Allow Threshold</Label>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                    {safeConfig.allowThreshold}%
                  </Badge>
                </div>
                <Slider
                  value={[safeConfig.allowThreshold]}
                  onValueChange={(value) => updateConfig("allowThreshold", value[0])}
                  max={100}
                  step={5}
                  className="mb-2"
                />
                <div className="text-xs text-slate-500">
                  Visitors with scores above this threshold are automatically allowed
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-slate-300">Challenge Threshold</Label>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">
                    {safeConfig.challengeThreshold}%
                  </Badge>
                </div>
                <Slider
                  value={[safeConfig.challengeThreshold]}
                  onValueChange={(value) => updateConfig("challengeThreshold", value[0])}
                  max={100}
                  step={5}
                  className="mb-2"
                />
                <div className="text-xs text-slate-500">
                  Visitors with scores between allow and challenge thresholds receive challenges
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-slate-300">Block Threshold</Label>
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/50">{safeConfig.blockThreshold}%</Badge>
                </div>
                <Slider
                  value={[safeConfig.blockThreshold]}
                  onValueChange={(value) => updateConfig("blockThreshold", value[0])}
                  max={100}
                  step={5}
                  className="mb-2"
                />
                <div className="text-xs text-slate-500">
                  Visitors with scores above this threshold are immediately blocked
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Performance Mode</Label>
                <Select
                  value={safeConfig.performanceMode}
                  onValueChange={(value: "ultra_fast" | "balanced" | "paranoid") =>
                    updateConfig("performanceMode", value)
                  }
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ultra_fast">Ultra Fast</SelectItem>
                    <SelectItem value="balanced">Balanced</SelectItem>
                    <SelectItem value="paranoid">Paranoid</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1">Controls processing speed vs accuracy trade-off</div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-300">Real-time Monitoring</Label>
                    <div className="text-xs text-slate-500">Enable live traffic monitoring</div>
                  </div>
                  <Switch
                    checked={safeConfig.enableRealTimeMonitoring}
                    onCheckedChange={(checked) => updateConfig("enableRealTimeMonitoring", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-300">Intelligence Engine</Label>
                    <div className="text-xs text-slate-500">Advanced pattern recognition</div>
                  </div>
                  <Switch
                    checked={safeConfig.enableIntelligenceEngine}
                    onCheckedChange={(checked) => updateConfig("enableIntelligenceEngine", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-300">Fingerprint Analysis</Label>
                    <div className="text-xs text-slate-500">Deep fingerprint inspection</div>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-slate-300">Behavioral Tracking</Label>
                    <div className="text-xs text-slate-500">Monitor user behavior patterns</div>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-700/50 flex justify-between">
            <Button variant="outline" size="sm" onClick={resetToDefaults}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset to Mode Defaults
            </Button>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                Test Configuration
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save Configuration
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Features */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <Lock className="mr-2 h-5 w-5 text-blue-500" />
            Security Features & Modules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                name: "Stealth Bot Detection",
                enabled: true,
                description: "Detect headless browsers and automation tools",
              },
              {
                name: "Fingerprint Analysis",
                enabled: true,
                description: "Advanced browser fingerprinting",
              },
              {
                name: "Rate Limiting",
                enabled: true,
                description: "Request rate limiting per IP/ASN",
              },
              {
                name: "Geo-blocking",
                enabled: false,
                description: "Block traffic from specific countries",
              },
              {
                name: "ASN Filtering",
                enabled: true,
                description: "Filter suspicious hosting providers",
              },
              {
                name: "Challenge System",
                enabled: true,
                description: "CAPTCHA and proof-of-work challenges",
              },
            ].map((feature, index) => (
              <div key={index} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-200">{feature.name}</h3>
                  <Switch defaultChecked={feature.enabled} />
                </div>
                <p className="text-xs text-slate-400">{feature.description}</p>
                <div className="mt-2">
                  <Badge
                    className={
                      feature.enabled
                        ? "bg-green-500/20 text-green-400 border-green-500/50"
                        : "bg-slate-500/20 text-slate-400 border-slate-500/50"
                    }
                  >
                    {feature.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  )
}
